import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const projects = [
  {
    category: 'Hotel Development',
    title: 'Multi-Floor Hotel Projects',
    description:
      'Full procurement and FF&E sourcing for multi-story hotel developments — from lobby furniture and room fittings to kitchen equipment and outdoor infrastructure.',
    image:
      'https://public.readdy.ai/ai/img_res/e924c1630523312a804861f7c54b4147.jpg',
    tag: 'Hotel & Hospitality',
  },
  {
    category: 'Villa Development',
    title: 'Premium Villa Complexes',
    description:
      'End-to-end sourcing for residential villa developments — high-spec materials, luxury furnishings, sanitary fittings, and complete interior supply packages.',
    image:
      'https://readdy.ai/api/search-image?query=luxury%20modern%20villa%20complex%20exterior%20dark%20stone%20facade%20with%20infinity%20pool%20warm%20lighting%20at%20twilight%2C%20premium%20residential%20architecture%20photography%2C%20minimal%20contemporary%20design%2C%20gold%20and%20charcoal%20tones&width=700&height=480&seq=proj_villa_1&orientation=landscape',
    tag: 'Residential',
  },
  {
    category: 'Vehicle Export',
    title: 'Fleet & EV Export Programs',
    description:
      'Large-scale vehicle export programs including electric vehicles and automobile fleets — factory sourcing, PDI, documentation, and sea freight to East Africa and the UAE.',
    image:
      'https://readdy.ai/api/search-image?query=fleet%20of%20sleek%20modern%20electric%20vehicles%20in%20dark%20industrial%20warehouse%20with%20warm%20amber%20overhead%20lighting%2C%20professional%20automotive%20commercial%20photography%2C%20dark%20moody%20atmosphere%2C%20no%20people&width=700&height=480&seq=proj_vehicle_1&orientation=landscape',
    tag: 'Automotive',
  },
  {
    category: 'Construction Supply',
    title: 'Machinery & Materials Supply',
    description:
      'Supply of heavy construction machinery and building materials for large infrastructure and real estate projects — excavators, cranes, steel, and construction consumables.',
    image:
      'https://readdy.ai/api/search-image?query=massive%20construction%20site%20at%20golden%20sunset%20with%20large%20yellow%20excavators%20and%20cranes%20silhouetted%20against%20dramatic%20amber%20sky%2C%20industrial%20infrastructure%20development%20photography%2C%20cinematic%20wide%20angle&width=700&height=480&seq=proj_const_1&orientation=landscape',
    tag: 'Infrastructure',
  },
];

export default function Projects() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="projects" className="py-32 bg-charcoal-light">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-16 transition-all duration-800 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-gold text-[10px] font-semibold tracking-[0.35em] uppercase mb-5 block">
            Portfolio
          </span>
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-light text-white leading-tight">
                Our Project<br />
                <em className="not-italic text-gold">Portfolio</em>
              </h2>
              <div className="section-divider mt-5" />
            </div>
            <p className="text-[#7A7A7A] text-sm max-w-md leading-relaxed tracking-wide">
              A cross-section of the sectors and deal types we execute for clients globally.
            </p>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project, i) => (
            <div
              key={project.title}
              className={`group relative overflow-hidden border border-charcoal-border hover:border-gold/40 transition-all duration-700 cursor-default ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 130}ms` }}
            >
              {/* Image */}
              <div className="w-full h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 h-64 bg-gradient-to-t from-charcoal-light via-charcoal-light/40 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-8 relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] text-gold tracking-[0.2em] uppercase font-medium">
                    {project.category}
                  </span>
                  <span className="text-[10px] text-charcoal-text border border-charcoal-border px-3 py-1 tracking-wider">
                    {project.tag}
                  </span>
                </div>
                <h3 className="font-heading text-xl text-white font-light mb-3 group-hover:text-gold transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-[#7A7A7A] text-sm leading-relaxed tracking-wide">
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
